/**
 * Date and time helpers for the command centre.
 *
 * Every "day" here is the viewer's LOCAL calendar day. Lessons are stored in
 * UTC, so a day's lessons are fetched with the UTC instants of local midnight
 * to local midnight (`localDayRange`), never with a bare `YYYY-MM-DD`. The
 * backend's datetime filter reads a bare date as UTC midnight, which silently
 * drops every lesson after 00:00 UTC.
 *
 * Copy follows the house style: UK English, 24-hour times, no dashes.
 */

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` for a local calendar day. */
export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local midnight for a `YYYY-MM-DD` key. */
export function fromDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function todayKey(): string {
  return toDateKey(new Date());
}

export function addDays(key: string, days: number): string {
  const d = fromDateKey(key);
  d.setDate(d.getDate() + days);
  return toDateKey(d);
}

/** Monday of the week containing `key`. */
export function startOfWeek(key: string): string {
  const d = fromDateKey(key);
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return toDateKey(d);
}

/** Seven consecutive day keys starting at `start`. */
export function weekDays(start: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** First day of the month containing `key`. */
export function startOfMonth(key: string): string {
  const d = fromDateKey(key);
  return toDateKey(new Date(d.getFullYear(), d.getMonth(), 1));
}

/**
 * UTC ISO bounds for local days `fromKey` through `toKey` inclusive, for the
 * backend's `date_from` / `date_to` datetime filters.
 */
export function localDayRange(
  fromKey: string,
  toKey: string = fromKey,
): { date_from: string; date_to: string } {
  const start = fromDateKey(fromKey);
  const end = new Date(fromDateKey(addDays(toKey, 1)).getTime() - 1);
  return { date_from: start.toISOString(), date_to: end.toISOString() };
}

/** The local day key an ISO instant falls on. */
export function dayKeyOf(iso: string): string {
  return toDateKey(new Date(iso));
}

/** `14:30` */
export function formatTime(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** `14:00 to 15:00`, or with a custom glue such as a thin space. */
export function formatTimeRange(iso: string, minutes: number, glue = " to "): string {
  const start = new Date(iso);
  const end = new Date(start.getTime() + minutes * 60_000);
  return `${formatTime(start)}${glue}${formatTime(end)}`;
}

/** `Tuesday, 22 September` */
export function formatLongDate(key: string): string {
  return fromDateKey(key).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/** `Tue 22 Sep` */
export function formatShortDate(key: string): string {
  return fromDateKey(key).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** `22 Sep` for an ISO instant. */
export function formatDayMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** `22 Sep 2026, 14:30` for an ISO instant. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}, ${formatTime(d)}`;
}

/** `September 2026` */
export function formatMonthYear(key: string): string {
  return fromDateKey(key).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

/** `Mon`, `Tue` ... */
export function weekdayShort(key: string): string {
  return fromDateKey(key).toLocaleDateString("en-GB", { weekday: "short" });
}

/** `M`, `T`, `W` ... */
export function weekdayInitial(key: string): string {
  return weekdayShort(key).charAt(0);
}

/** `Tuesday` */
export function weekdayLong(key: string): string {
  return fromDateKey(key).toLocaleDateString("en-GB", { weekday: "long" });
}

/** Compact age for tabular trailing slots: `now`, `12m`, `3h`, `2d`, `5w`. */
export function ageing(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return `${Math.floor(day / 7)}w`;
}

/** Age in whole days, for severity rules. */
export function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : Math.floor(ms / DAY_MS);
}

/** `just now`, `12m ago`, `3h ago`, `2d ago`. */
export function timeAgo(iso: string): string {
  const a = ageing(iso);
  if (!a) return "";
  return a === "now" ? "just now" : `${a} ago`;
}

/** `now`, `in 12m`, `in 3h`, `in 2d`. */
export function countdown(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return "soon";
  if (ms <= 0) return "now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.round(hrs / 24)}d`;
}

/** Whole local days from today until the day of `iso`. Negative when past. */
export function daysUntil(iso: string): number {
  const due = fromDateKey(dayKeyOf(iso)).getTime();
  const today = fromDateKey(todayKey()).getTime();
  return Math.round((due - today) / DAY_MS);
}

/**
 * Due phrase for homework: `overdue by 2 days`, `due today`, `due tomorrow`,
 * `due in 3 days`, `due 4 Oct`.
 */
export function duePhrase(iso: string): string {
  const diff = daysUntil(iso);
  if (diff < -1) return `overdue by ${-diff} days`;
  if (diff === -1) return "overdue since yesterday";
  if (diff === 0) return "due today";
  if (diff === 1) return "due tomorrow";
  if (diff < 7) return `due in ${diff} days`;
  return `due ${formatDayMonth(iso)}`;
}

/** `morning` / `afternoon` / `evening` for the current local hour. */
export function partOfDay(d: Date = new Date()): "morning" | "afternoon" | "evening" {
  const h = d.getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}

/** Minutes since local midnight. */
export function minutesIntoDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Plural helper: `plural(3, "lesson")` gives `3 lessons`. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
